import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha, Chip, LinearProgress } from '@mui/material';
import { HowToReg, AccessTime, TrendingUp } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const ApprovalNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Node color - Orange theme
  const nodeColor = theme.palette.warning.main;
  const textColor = theme.palette.common.white;
  const borderColor = selected ? theme.palette.secondary.main : 'transparent';

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'approved': return theme.palette.success.main;
      case 'rejected': return theme.palette.error.main;
      case 'pending': return theme.palette.warning.main;
      case 'escalated': return theme.palette.secondary.main;
      default: return theme.palette.grey[500];
    }
  };

  const getTimeoutDisplay = (timeout) => {
    if (!timeout) return null;
    if (typeof timeout === 'number') {
      return timeout > 60 ? `${Math.floor(timeout / 60)}h` : `${timeout}m`;
    }
    return timeout;
  };

  const isOverdue = data.status === 'pending' && data.timeoutProgress > 80;

  return (
    <Paper
      elevation={selected ? 12 : 4}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      sx={{
        padding: 2.5,
        borderRadius: 3,
        minWidth: 200,
        minHeight: 100,
        position: 'relative',
        overflow: 'visible',
        cursor: 'move',
        background: isDark
          ? `linear-gradient(145deg, ${alpha('#0D2E4D', 0.9)}, ${alpha('#0D2E4D', 0.8)})`
          : `linear-gradient(145deg, ${alpha(nodeColor, 0.95)}, ${alpha(nodeColor, 0.85)})`,
        backdropFilter: 'blur(10px)',
        border: selected
          ? `2px solid ${theme.palette.secondary.main}`
          : `1px solid ${alpha(nodeColor, 0.3)}`,
        transition: theme.transitions.create([
          'transform', 'box-shadow', 'border'
        ], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          transform: 'translateY(-4px) scale(1.02)',
          boxShadow: theme.shadows[16],
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
          '& .approval-icon': {
            transform: 'scale(1.2)',
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 'inherit',
          background: `radial-gradient(circle at top right, ${alpha(theme.palette.warning.main, 0.1)}, transparent)`,
          pointerEvents: 'none',
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Approval node: ${data.label || 'Approval Process'}`}
      aria-selected={selected}
    >
      {/* Handles */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: `linear-gradient(135deg, ${nodeColor} 0%, ${theme.palette.warning.dark} 100%)`,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 1.5,
          mx: -1,
          mt: -1,
          borderRadius: 2,
          background: alpha(nodeColor, 0.15),
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            p: 0.5,
            borderRadius: 1.5,
            background: `linear-gradient(135deg, ${nodeColor} 0%, ${theme.palette.warning.dark} 100%)`,
            display: 'flex',
            color: theme.palette.common.white,
            boxShadow: `0 2px 8px ${alpha(nodeColor, 0.3)}`,
          }}>
            <HowToReg className="approval-icon" sx={{ fontSize: 18, transition: 'transform 0.3s ease' }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="600" color={textColor}>
            {data.label || 'Approval Process'}
          </Typography>
        </Box>
        {data.status && (
          <Chip
            label={data.status}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              backgroundColor: alpha(getStatusColor(data.status), 0.2),
              color: getStatusColor(data.status),
              border: `1px solid ${alpha(getStatusColor(data.status), 0.4)}`,
            }}
          />
        )}
      </Box>

      {/* Description */}
      {data.description && (
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            color: alpha(textColor, 0.85),
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {data.description}
        </Typography>
      )}

      {/* Approval Fields */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        {data.approver && (
          <Chip
            label={`Approver: ${data.approver}`}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.info.main, 0.2),
              color: theme.palette.info.dark,
              border: `1px solid ${alpha(theme.palette.info.main, 0.4)}`,
            }}
          />
        )}
        {data.timeout && (
          <Chip
            icon={<AccessTime sx={{ fontSize: '12px !important' }} />}
            label={getTimeoutDisplay(data.timeout)}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(isOverdue ? theme.palette.error.main : theme.palette.warning.main, 0.2),
              color: isOverdue ? theme.palette.error.dark : theme.palette.warning.dark,
              border: `1px solid ${alpha(isOverdue ? theme.palette.error.main : theme.palette.warning.main, 0.4)}`,
            }}
          />
        )}
        {data.escalation && (
          <Chip
            icon={<TrendingUp sx={{ fontSize: '12px !important' }} />}
            label={data.escalation}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.secondary.main, 0.2),
              color: theme.palette.secondary.dark,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
            }}
          />
        )}
      </Box>

      {/* Timeout Progress Bar */}
      {data.status === 'pending' && data.timeoutProgress !== undefined && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: alpha(textColor, 0.7) }}>
              Time Remaining
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isOverdue ? theme.palette.error.main : theme.palette.warning.main,
                fontWeight: 600
              }}
            >
              {100 - (data.timeoutProgress || 0)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={data.timeoutProgress || 0}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.common.white, 0.2),
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
                background: isOverdue
                  ? `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`
                  : `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
              }
            }}
          />
        </Box>
      )}

      {/* Approval History or Comments */}
      {data.comments && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            backgroundColor: alpha(textColor, 0.1),
            borderRadius: 1,
            border: `1px solid ${alpha(textColor, 0.2)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: alpha(textColor, 0.8),
              fontSize: '0.7rem',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '160px',
            }}
          >
            Comments: {data.comments.length > 30 ? `${data.comments.substring(0, 30)}...` : data.comments}
          </Typography>
        </Box>
      )}

      {/* Status Indicator with Animation */}
      <Box
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: data.status
            ? `linear-gradient(135deg, ${getStatusColor(data.status)} 0%, ${alpha(getStatusColor(data.status), 0.8)} 100%)`
            : `linear-gradient(135deg, ${nodeColor} 0%, ${theme.palette.warning.dark} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.palette.common.white,
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
          border: `2px solid ${theme.palette.background.paper}`,
          animation: data.status === 'pending' ? 'pulse 2s infinite' : 'none',
          '@keyframes pulse': {
            '0%': {
              boxShadow: `0 4px 12px ${alpha(nodeColor, 0.4)}`,
            },
            '50%': {
              boxShadow: `0 4px 20px ${alpha(nodeColor, 0.6)}`,
            },
            '100%': {
              boxShadow: `0 4px 12px ${alpha(nodeColor, 0.4)}`,
            },
          }
        }}
      >
        <HowToReg sx={{ fontSize: 14 }} />
      </Box>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: `linear-gradient(135deg, ${nodeColor} 0%, ${theme.palette.warning.dark} 100%)`,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />
    </Paper>
  );
});

ApprovalNode.displayName = 'ApprovalNode';

export default ApprovalNode;